/**
 * React App Entry Point
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { AuthProvider } from './contexts/AuthContext';
import { HotkeyProvider } from './lib/hotkeys';
import { sendClientLog } from './lib/infra/clientLog';
import './globals.css';
import './styles.css';

// ── Global error handlers (fire before React mounts) ───────────────
window.onerror = (_msg, source, lineno, colno, error) => {
  sendClientLog('error', error?.message || String(_msg), {
    source: `${source || 'unknown'}:${lineno}:${colno}`,
    stack: error?.stack,
  });
};

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  sendClientLog('error', message, {
    source: 'unhandledrejection',
    stack: reason instanceof Error ? reason.stack : undefined,
  });
});

// Router basename for subdirectory deployment (e.g., "/decoy_folder/dashboard")
const routerBasename = import.meta.env.VITE_ROUTER_BASE || '';
const isMultiTenant = import.meta.env.VITE_MULTI_TENANT === 'true';

/**
 * Multi-tenant routes: one canonical /app/* shell with compatibility redirects.
 */
function MultiTenantApp() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Navigate
            to="/login"
            replace
          />
        }
      />
      <Route
        path="/login"
        element={<App />}
      />
      <Route
        path="/app"
        element={
          <Navigate
            to="/app/value-dashboard"
            replace
          />
        }
      />
      <Route
        path="/app/*"
        element={<App />}
      />

      {/* Legacy MT URLs — redirect into the canonical app shell */}
      <Route
        path="/register"
        element={
          <Navigate
            to="/login"
            replace
          />
        }
      />
      <Route
        path="/rooms"
        element={
          <Navigate
            to="/app/value-dashboard"
            replace
          />
        }
      />
      <Route
        path="/room/:roomId"
        element={
          <Navigate
            to="/app/value-dashboard"
            replace
          />
        }
      />
      <Route
        path="/admin"
        element={
          <Navigate
            to="/app/admin"
            replace
          />
        }
      />
      <Route
        path="/admin/users"
        element={
          <Navigate
            to="/app/admin"
            replace
          />
        }
      />
      <Route
        path="/admin/credentials"
        element={
          <Navigate
            to="/app/admin"
            replace
          />
        }
      />
      <Route
        path="/admin/activity"
        element={
          <Navigate
            to="/app/admin"
            replace
          />
        }
      />

      <Route
        path="*"
        element={
          <Navigate
            to="/login"
            replace
          />
        }
      />
    </Routes>
  );
}

/**
 * Single-user routes: existing App at all paths
 */
function SingleUserApp() {
  return (
    <Routes>
      {/* Login route */}
      <Route
        path="/"
        element={<App />}
      />

      {/* Authenticated app routes */}
      <Route
        path="/app"
        element={<App />}
      />

      {/* Explore hierarchy routes */}
      <Route
        path="/app/explore"
        element={<App />}
      />
      <Route
        path="/app/explore/:category"
        element={<App />}
      />
      <Route
        path="/app/explore/:category/:series"
        element={<App />}
      />
      <Route
        path="/app/explore/:category/:series/:event"
        element={<App />}
      />

      {/* Direct market route */}
      <Route
        path="/app/market/:ticker"
        element={<App />}
      />

      {/* Other views */}
      <Route
        path="/app/:view"
        element={<App />}
      />

      {/* Catch-all redirect */}
      <Route
        path="*"
        element={
          <Navigate
            to="/"
            replace
          />
        }
      />
    </Routes>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <ActivityProvider>
        <HotkeyProvider>
          <ErrorBoundary>
            <BrowserRouter basename={routerBasename}>
              {isMultiTenant ? (
                <AuthProvider>
                  <MultiTenantApp />
                </AuthProvider>
              ) : (
                <SingleUserApp />
              )}
            </BrowserRouter>
          </ErrorBoundary>
        </HotkeyProvider>
      </ActivityProvider>
    </ThemeProvider>
  </React.StrictMode>
);
